import { AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, OnInit, ViewChild, ViewChildren, QueryList } from "@angular/core";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { ModuleManager } from "src/shared/models/module-manager";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { NavigationService } from 'src/app/navigation/navigation.service';
import { SupervisorService } from "./supervisor.service";
import { SupervisorObject } from "./supervisor.model";
import OverlayScrollbars from 'overlayscrollbars';


@Component({
    selector: 'cxone-games-tracker',
    templateUrl: './cxone-games-tracker.component.html',
    styleUrl: './cxone-games-tracker.component.scss',
    providers: [SupervisorService]
})

export class CxoneGamesTrackerComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild("gameContainer") gameContainer: ElementRef;
    @ViewChildren('trackerCardsDiv') trackerCardsDiv!: QueryList<ElementRef>;

    private observerTrackerCards!: IntersectionObserver;
    public loadedIds: Set<number> = new Set<number>();
    public noData = false;
    public isDataLoading = false;
    public dataLoaded = false;
    /** Error state trigger */
    public noPMLicense = false;
    public somethingWentWrong = false;
    public filteredSupervisors: SupervisorObject[] = [];
    public agentData: SupervisorObject[] = [];
    public searchTerm: string = '';
    private loginId = 0;
    public supervisors: SupervisorObject[] = [];
    currentView: number;
    activeLoadings = 0;
    public reloadingAgents = false;
    isSeeMoreOpen = false;
    agentSelectedScroll: number;

    scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "scrollbar-games"
        }
    };

    hiddenScrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            visibility: "hidden",
            autoHideDelay: 100,
            theme: "scrollbar-challenge",
        },
    };

    constructor(
        public cvLocalizationService: CVLocalizationService,
        private zone: NgZone,
        private navigationService: NavigationService,
        private supervisorService: SupervisorService
    ) { }

    async ngOnInit() {
        this.moduleManager.showLoader = true;

        this.moduleManager.setOptions({
            onSettingsApplied: settings => {
                this.loadData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.FiveMinute,
            onRefresh: (isAutoRefresh) => {
                this.reloadingAgents = true;
                this.loadedIds.clear();
                this.zone.run(() => this.loadData(isAutoRefresh))
            },
            canRefresh: () => true,
        });

        await this.loadLocalization();
        this.noPMLicense = !this.navigationService.hasPartnerPMAccess;
        this.supervisorService.setModuleManager(this.moduleManager);
    }

    ngAfterViewChecked() {
        this.supervisorService.setSeeMoreContainer(this.gameContainer);
        this.supervisorService.getCurrentView().subscribe(view => {
            this.currentView = view;
        });
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection('Games_Module');
    }

    public async loadData(isAutoRefresh: boolean) {
        if (!this.noPMLicense) {
            if (this.isDataLoading){
                return; // don't have multiple outgoing requests
            }
            this.isDataLoading = true;
            this.dataLoaded = false; 
            this.moduleManager.showLoader = true;
            this.somethingWentWrong = false;
            this.searchTerm = null;

            try {
                let response = await this.supervisorService.getSupervisorsData(this.moduleKey, this.loginId);
                this.supervisors = response?.data || [];
                this.filteredSupervisors = [...this.supervisors];
            } catch (err) {
                this.moduleManager.handleLoadError(err, isAutoRefresh);
                this.somethingWentWrong = true;
                return;
            }
            finally {
                this.moduleManager.showLoader = false;
                this.dataLoaded = true;
                this.noData = (this.supervisors === undefined || this.supervisors === null || this.supervisors.length === 0) && !this.somethingWentWrong;
                this.moduleManager.setAutoRefreshReady();
                this.isDataLoading = false;
                if (this.reloadingAgents) {
                    this.initializeIntersectionObserver();
                }
            }
        }
    }

    public async loadAgentsInformation(agentId: number) {
        if (!this.noPMLicense) {
            this.activeLoadings++;
            this.moduleManager.showLoader = true;
            this.somethingWentWrong = false;
            try {
                let agentResponse = await this.supervisorService.getSupervisorsDataByAgent(this.moduleKey, this.loginId, agentId);
                if (this.agentData.find(item => item?.LoginID === agentResponse?.LoginID)) {
                    //If the agent is already loaded just update the information
                    const index = this.agentData.findIndex(item => item?.LoginID === agentResponse?.LoginID);
                    this.agentData[index] = agentResponse;
                } else {
                    //If the agent is being loaded for the first time, just add it to the array
                    this.agentData.push(agentResponse);
                }
            } catch (err) {
                this.moduleManager.handleLoadError(err);
                this.somethingWentWrong = true;
                return;
            }
            finally {
                this.activeLoadings--;
                if (this.activeLoadings === 0) {
                    this.moduleManager.showLoader = false;
                }
                this.noData = this.agentData === undefined || this.agentData === null;
                this.moduleManager.setAutoRefreshReady();
                this.isDataLoading = false;
            }
        }
    }

    applySearchFilter(): void {
        if (!this.searchTerm) {
            this.filteredSupervisors = [...this.supervisors];
        } else {
            this.filteredSupervisors = this.agentData?.filter(supervisor =>
                supervisor.GroupName?.toLowerCase().includes(this.searchTerm.trim().toLowerCase())
            );
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("Games_Module", key, defaultValue);
        return baseString;
    }

    get isSearchHidden(): boolean {
        const hidden = this.currentView !== 0;
        return hidden;
    }

    ngAfterViewInit() {
        this.trackerCardsDiv.changes.subscribe(() => this.initializeIntersectionObserver());
    }

    initializeIntersectionObserver() {
        //Similar to games widget, this will handle the intersection of the widget and the agent cards
        const options: IntersectionObserverInit = {
            root: document.querySelector('#agentsDiv') as Element,
            rootMargin: '0px',
            threshold: 0.1,
        };
        this.observerTrackerCards = new IntersectionObserver(
            this.handleIntersection.bind(this),
            options
        );
        this.trackerCardsDiv.forEach((div) => this.observerTrackerCards.observe(div.nativeElement));
    }

    handleIntersection(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isSeeMoreOpen) {
                const id = parseInt(entry.target.id);
                if (!this.loadedIds.has(id)) {
                    this.loadAgentsInformation(id);
                    this.loadedIds.add(id);
                }
            }
        });
    }

    findObjectByAgentID(loginID) {
        return this.agentData?.find(item => item?.LoginID === loginID);
    };

    onSeeMoreStatusChange(isOpen: boolean) {
        this.isSeeMoreOpen = isOpen;

        if (this.isSeeMoreOpen == false) {
            //This will return the module manager to the previous configuration after being overwritten in the see more view.
            this.moduleManager.setOptions({
                onSettingsApplied: settings => {
                    this.loadData(false);
                },
                hasRefresh: true,
                refreshType: ModuleManagerRefreshType.FiveMinute,
                onRefresh: (isAutoRefresh) => {
                    this.reloadingAgents = true;
                    this.loadedIds.clear();
                    this.zone.run(() => this.loadData(isAutoRefresh))
                },
                canRefresh: () => true,
            });
            this.scrollToSupervisor(this.agentSelectedScroll); //To show the correct agent when using back button
        }
    }

    syncAgent(updatedAgent) {
        //With this, we will update the value in the agentData array used in the card View from the see more view
        const index = this.agentData.findIndex(a => a.LoginID === updatedAgent.LoginID);
        this.agentData[index] = updatedAgent;
    }

    scrollToSupervisor(loginID: number) {
        //This will handle the selected agent card to be shown at the end of the widget when returning from see more view.
        const card = this.trackerCardsDiv.find(div =>
            div.nativeElement.id === String(loginID)
        );

        card.nativeElement.scrollIntoView({ behavior: 'instant', block: 'end' });
    }

    handleSupervisorSelected(loginID: number) {
        this.agentSelectedScroll = loginID; //Update the agent Selected after clicking see more button
    }
}
